/**
 * ProductMediaThumb — Netflix/YouTube style product thumbnail.
 * Auto-plays video when scrolled into viewport (IntersectionObserver).
 * Supports image, video, or mixed (image + video overlay).
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Play } from "lucide-react";
import { resolveMedia } from "../../pages/PublicMenuPage";

function useInViewport(options = {}) {
  const [isInViewport, setIsInViewport] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = entry.isIntersecting;
        setIsInViewport(inView);
        if (inView && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      },
      {
        threshold: options.threshold || 0.5,
        rootMargin: options.rootMargin || '50px',
        ...options
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin, hasBeenVisible]);

  return { ref, isInViewport, hasBeenVisible };
}

export function ProductMediaThumb({ product, isDark }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const mediaBg = isDark ? '#1a1a1a' : '#f3f4f6';

  const { ref: viewportRef, isInViewport } = useInViewport({ threshold: 0.5, rootMargin: '50px' });

  const isVideo = (product.media_type === "video" && product.video_url) ||
                  (product.video_url && product.video_url.match(/\.(mp4|webm|mov)$/i));
  const videoSrc = resolveMedia(product.video_url);
  const imageSrc = product.image_url ? resolveMedia(product.image_url) : null;

  // Auto-play: When product enters viewport, start video
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;

    if (isInViewport) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.log('Autoplay blocked:', err);
          });
      }
    } else if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    }
  }, [isInViewport, isVideo, isPlaying]);

  const startVideo = useCallback(() => {
    if (!isVideo || isPlaying || videoError) return;
    setIsPlaying(true);
  }, [isVideo, isPlaying, videoError]);

  const stopVideo = useCallback(() => {
    if (!isPlaying) return;
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying && videoRef.current) {
      videoRef.current.play().catch(() => setVideoError(true));
    }
  }, [isPlaying]);

  if (!product.image_url && !product.video_url) {
    return (
      <div className="w-24 sm:w-32 flex-shrink-0 relative overflow-hidden flex items-center justify-center rounded-lg"
           style={{ minHeight: 100, backgroundColor: mediaBg }}>
        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: isDark ? '#333' : '#e5e7eb' }} />
      </div>
    );
  }

  const handleMouseEnter = () => { setIsHovering(true); startVideo(); };
  const handleMouseLeave = () => { setIsHovering(false); };
  const isMobile = typeof window !== 'undefined' && 'ontouchstart' in window;

  return (
    <div
      ref={(node) => { containerRef.current = node; viewportRef.current = node; }}
      className={`w-24 sm:w-32 flex-shrink-0 relative overflow-hidden group rounded-lg cursor-pointer transition-transform duration-300 ${isInViewport ? 'scale-100' : 'scale-95 opacity-90'}`}
      style={{ minHeight: 100, backgroundColor: mediaBg }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={isMobile && isVideo ? () => setIsPlaying(p => !p) : undefined}
      data-testid={`product-media-${product.id}`}
    >
      {imageSrc && (
        <img
          src={imageSrc}
          alt={product.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out ${isPlaying ? 'opacity-0' : 'opacity-100'} ${isInViewport ? 'scale-105' : 'scale-100'}`}
          style={{ zIndex: 2 }}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
        />
      )}

      {!imageSrc && isVideo && !isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            zIndex: 2,
            background: `linear-gradient(135deg, ${isDark ? '#2d1f3d' : '#fef3c7'} 0%, ${isDark ? '#1a1a2e' : '#fde68a'} 50%, ${isDark ? '#16213e' : '#fcd34d'} 100%)`
          }}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
               style={{
                 backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                 color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'
               }}>
            {product.name ? product.name.charAt(0).toUpperCase() : '?'}
          </div>
        </div>
      )}

      {isVideo && !isPlaying && !videoError && (
        <div className="absolute inset-0 flex items-center justify-center transition-opacity" style={{ zIndex: 3 }}>
          <div className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:bg-black/70 transition-all shadow-lg">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}

      {isVideo && (
        <video
          ref={videoRef}
          src={videoSrc}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
          style={{ zIndex: 4 }}
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => { setIsPlaying(false); setVideoError(true); }}
        />
      )}

      {videoError && !imageSrc && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 5, backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6' }}>
          <div className="text-center px-2">
            <div className="w-8 h-8 mx-auto mb-1 rounded-full flex items-center justify-center" style={{ backgroundColor: isDark ? '#333' : '#e5e7eb' }}>
              <Play className="w-4 h-4 opacity-50" style={{ color: isDark ? '#888' : '#666' }} />
            </div>
            <span className="text-xs opacity-60" style={{ color: isDark ? '#aaa' : '#666' }}>Video</span>
          </div>
        </div>
      )}

      {imageSrc && !imgLoaded && !imgError && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
          <div className="w-5 h-5 rounded-full animate-pulse" style={{ backgroundColor: isDark ? '#333' : '#ddd' }} />
        </div>
      )}
    </div>
  );
}
